import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Text,
  Modal,
  ActivityIndicator,
} from 'react-native';
import { H1, H2, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getColor, getSpacing, getBorderRadius } from '../theme/adminTheme';
import Table from '../components/common/Table';
import { Ionicons } from '@expo/vector-icons';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getEvents } from '../api/events';

const EventsManageScreen = ({ navigation }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Attendees state
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      return '—';
    } catch (error) {
      console.error('Date formatting error:', error);
      return '—';
    }
  };

  const fetchAllEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      // data might be wrapped or just an array
      setEvents(data || []);
    } catch (error) {
      console.error('[EventsManage] Fetch error:', error);
      Alert.alert('Error', 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Refresh events when returning to this screen
    const unsubscribe = navigation.addListener('focus', () => {
        fetchAllEvents();
    });
    return unsubscribe;
  }, [navigation, fetchAllEvents]);

  const confirmDelete = (event) => {
    const message = `Are you sure you want to delete ${event.title}?`;

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        handleDelete(event);
      }
    } else {
      Alert.alert(
        'Delete Event',
        message,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            onPress: () => handleDelete(event),
            style: 'destructive'
          },
        ]
      );
    }
  };

  const handleDelete = async (event) => {
    try {
      const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
      if (!token) throw new Error('No auth token found');

      const response = await fetch(`${getApiUrl()}/events/admin/${event?.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        if (Platform.OS === 'web') window.alert('Event deleted successfully');
        else Alert.alert('Success', 'Event deleted successfully');
        fetchAllEvents();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || 'Failed to delete event';
        if (Platform.OS === 'web') window.alert(`Error: ${errorMsg}`);
        else Alert.alert('Error', errorMsg);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      if (Platform.OS === 'web') window.alert(`Error: ${error.message || 'Failed to delete'}`);
      else Alert.alert('Error', 'Failed to delete event');
    }
  };

  const fetchAttendees = async (event) => {
    try {
      setCurrentEvent(event);
      setAttendeesLoading(true);
      setShowAttendeesModal(true);
      
      const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/events/${event.id}/attendees`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setAttendees(data || []);
    } catch (error) {
      console.error('Error fetching attendees:', error);
      Alert.alert('Error', 'Failed to load attendees');
    } finally {
      setAttendeesLoading(false);
    }
  };

  const exportAttendees = () => {
    if (!attendees || attendees.length === 0) return;

    // Create CSV content
    const header = ['Name', 'Email', 'Phone', 'Role', 'Status', 'Registration Date'];
    const rows = attendees.map(a => [
      `"${a.user?.name || ''}"`,
      `"${a.user?.email || ''}"`,
      `"${a.user?.phone || 'N/A'}"`,
      `"${a.user?.role || ''}"`,
      `"${a.user?.status || ''}"`,
      `"${new Date(a.createdAt).toLocaleDateString()}"`
    ]);

    const csvContent = [header, ...rows].map(e => e.join(",")).join("\n");

    if (Platform.OS === 'web') {
      try {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `attendees_${currentEvent.title.replace(/\s+/g, '_')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Web export error:', err);
      }
    } else {
      Alert.alert('Export (CSV)', csvContent.substring(0, 500) + '...');
    }
  };

  return (
    <AdminLayout>
      <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <H1>Event Management</H1>
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => navigation.navigate('EventAdd')}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <ButtonText style={styles.addButtonText}>Create Activity</ButtonText>
          </TouchableOpacity>
        </View>

        <View style={styles.tableContainer}>
          <Table
            data={events || []}
            columns={[
              {
                header: 'Date',
                field: 'event_date',
                width: '15%',
                render: (_, item) => (
                  <Text style={styles.dateText}>
                    {formatDate(item.event_date)}
                    {item.event_end_date && formatDate(item.event_end_date) !== formatDate(item.event_date) ? 
                      ` \n- ${formatDate(item.event_end_date)}` : ''}
                  </Text>
                ),
              },
              {
                header: 'Title & Vibe',
                field: 'title',
                width: '35%',
                render: (_, item) => (
                  <View style={styles.contentContainer}>
                    <Text style={styles.contentText} numberOfLines={2}>{item.title}</Text>
                    <View style={styles.vibePill}>
                       <Text style={styles.vibeText}>{item.vibe}</Text>
                    </View>
                  </View>
                ),
              },
              {
                header: 'Type & Location',
                field: 'type',
                width: '25%',
                render: (_, item) => (
                  <View style={styles.contentContainer}>
                    <Text style={styles.subText}>{item.type === 'virtual' ? 'Virtual' : 'In Person'}</Text>
                    <Text style={[styles.contentText, { fontSize: 12 }]} numberOfLines={2}>
                      {item.location_name || item.virtual_link || 'TBA'}
                    </Text>
                  </View>
                ),
              },
              {
                header: 'Actions',
                field: 'id',
                width: '25%',
                render: (_, item) => (
                  <View style={styles.actionButtons}>
                    <TouchableOpacity
                      style={styles.attendeesButton}
                      onPress={() => fetchAttendees(item)}
                    >
                      <Ionicons name="people-outline" size={14} color="#6366F1" />
                      <Text style={styles.attendeesButtonText}>Users</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={() => navigation.navigate('EventAdd', { editEvent: item })}
                    >
                      <Ionicons name="pencil-outline" size={14} color={getColor('blue.600')} />
                      <Text style={styles.editButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={() => confirmDelete(item)}
                    >
                      <Ionicons name="trash-outline" size={14} color={getColor('danger.600')} />
                    </TouchableOpacity>
                  </View>
                ),
              }
            ]}
            loading={loading}
          />
        </View>
      </ScrollView>

      {/* Attendees Modal */}
      <Modal
        visible={showAttendeesModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAttendeesModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <View>
                <H2 style={styles.modalTitle}>Activity Registry</H2>
                <Caption style={{ color: '#64748b' }}>{currentEvent?.title}</Caption>
              </View>
              <TouchableOpacity onPress={() => setShowAttendeesModal(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>

            {attendeesLoading ? (
              <View style={styles.modalLoading}>
                <ActivityIndicator color="#6366F1" />
              </View>
            ) : (
              <>
                <View style={styles.exportSection}>
                  <Text style={styles.attendeeCount}>{attendees.length} Joined</Text>
                  <TouchableOpacity style={styles.exportBtn} onPress={exportAttendees}>
                    <Ionicons name="download-outline" size={16} color="#6366F1" />
                    <Text style={styles.exportBtnText}>Export CSV</Text>
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.attendeeList}>
                  {attendees.map((a, idx) => (
                    <View key={a.id || idx} style={styles.attendeeRow}>
                      <View style={styles.attendeeMain}>
                        <Text style={styles.attendeeName}>{a.user?.name}</Text>
                        <Text style={styles.attendeeEmail}>{a.user?.email}</Text>
                      </View>
                      <View style={styles.attendeeMeta}>
                        <View style={[styles.roleBadge, { backgroundColor: a.user?.role === 'mentor' ? '#EEF2FF' : '#F8FAFC' }]}>
                          <Text style={[styles.roleText, { color: a.user?.role === 'mentor' ? '#6366F1' : '#64748b' }]}>{a.user?.role}</Text>
                        </View>
                      </View>
                    </View>
                  ))}
                  {attendees.length === 0 && (
                    <View style={styles.emptyAttendees}>
                      <Caption>No users have joined this activity yet.</Caption>
                    </View>
                  )}
                </ScrollView>
              </>
            )}
          </View>
        </View>
      </Modal>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getSpacing('lg'),
    paddingBottom: getSpacing('md'),
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: getColor('primary.600'),
    paddingHorizontal: getSpacing('md'),
    paddingVertical: getSpacing('sm'),
    borderRadius: getBorderRadius('md'),
    gap: getSpacing('xs'),
  },
  addButtonText: {
    color: getColor('background.default'),
  },
  tableContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: getSpacing('md'),
    borderRadius: getBorderRadius('lg'),
    overflow: 'hidden',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: getSpacing('xs'),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: getSpacing('sm'),
    paddingVertical: 8,
    gap: getSpacing('xs'),
    backgroundColor: getColor('blue.50'),
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: getColor('blue.200'),
  },
  editButtonText: {
    color: '#2563EB',
    fontSize: 12,
    fontWeight: '600',
  },
  attendeesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: getSpacing('sm'),
    paddingVertical: 8,
    gap: getSpacing('xs'),
    backgroundColor: '#EEF2FF',
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: '#C7D2FE',
  },
  attendeesButtonText: {
    color: '#6366F1',
    fontSize: 12,
    fontWeight: '600',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: getSpacing('sm'),
    paddingVertical: 8,
    gap: getSpacing('xs'),
    backgroundColor: getColor('danger.50'),
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: getColor('danger.100'),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 500,
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0F172A',
  },
  modalLoading: {
    padding: 40,
    alignItems: 'center',
  },
  exportSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    paddingHorizontal: 20,
    backgroundColor: '#F8FAFC',
  },
  attendeeCount: {
    fontSize: 14,
    fontWeight: '700',
    color: '#475569',
  },
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  exportBtnText: {
    color: '#6366F1',
    fontWeight: '700',
    fontSize: 12,
  },
  attendeeList: {
    padding: 10,
  },
  attendeeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  attendeeMain: {
    flex: 1,
  },
  attendeeName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E293B',
  },
  attendeeEmail: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  roleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  roleText: {
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  emptyAttendees: {
    padding: 40,
    alignItems: 'center',
  },
  deleteButtonText: {
    color: getColor('danger.600'),
    fontSize: 12,
    fontWeight: '600',
  },
  contentContainer: {
    justifyContent: 'center',
    paddingVertical: getSpacing('xs'),
  },
  contentText: {
    color: getColor('gray.900'),
    fontSize: 14,
    fontWeight: '600'
  },
  dateText: {
    color: getColor('gray.900'),
    fontSize: 14,
    fontWeight: '500',
  },
  subText: {
    fontSize: 12,
    color: getColor('gray.500'),
    marginBottom: 2,
  },
  vibePill: {
    alignSelf: 'flex-start',
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginTop: 4,
  },
  vibeText: {
    fontSize: 10,
    color: '#6366F1',
    fontWeight: 'bold',
  }
});

export default EventsManageScreen;
